import { dateOf, esc } from './sbchart.ts';

type Data = Record<string, any>;
export type DeathEntityKind = 'plot' | 'region' | 'firm' | 'project' | 'person' | 'case' | 'hospital';
export interface DeathLinks { entity(kind: DeathEntityKind, id: number, label: string): string }
export interface DeathDisplayContext { here?: boolean; alive?: boolean; goneHow?: string }
type Ref = { kind: DeathEntityKind; id: number; label: string };
type Part = string | Ref;
type Row = { label: string; parts: Part[] };
type Section = { title: string; rows: Row[] };

const id = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= 0xffff_ffff;
const isDay = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
const day = (v: unknown) => isDay(v) ? dateOf(v) : '日期未知';
const known = (v: unknown) => typeof v === 'string' && v ? v : '未知';
const number = (v: unknown, digits = 1) => typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v.toFixed(digits) : '未知';
const risk = (v: unknown) => typeof v === 'number' && Number.isFinite(v) && v >= 0 ? `${v === 0 ? '0' : (v * 100).toPrecision(6)}%` : '未知';
const factor = (v: unknown) => typeof v === 'number' && Number.isFinite(v) ? v.toPrecision(6) : '未知';
const row = (label: string, ...parts: Part[]): Row => ({ label, parts });
const ref = (kind: DeathEntityKind, value: unknown, label: string): Part => id(value) ? { kind, id: value, label: `${label} #${value}` } : `${label}未知`;
const triggers: Record<string, string> = {
  population_mixed_risk: '日常混合生存风险', external_age_risk: '外地年龄生存风险', work_accident: '工伤事故身亡',
  construction_collapse: '施工中倒塌身亡', building_collapse: '建筑倒塌身亡', homicide: '命案遇害',
  road_accident: '交通事故身亡', suicide: '自杀（模拟调查认定）', unknown: '未记录具体触发源',
};

/** 不在城不等于死亡。明确alive优先;旧person2没有alive时才用here=true确认在世。 */
function details(view: Data | null | undefined, context: DeathDisplayContext): Section[] {
  if (context.alive === true || (context.alive == null && context.here === true)) return [];
  const r = view?.observed === true && view.record && typeof view.record === 'object' ? view.record as Data : null;
  if (!r) return context.alive === false || context.goneHow === '去世'
    ? [{ title: '死亡记录', rows: [row('具体触发源', '未知；未保留实际死亡快照，不从年龄、病名或旧故事补猜。')] }] : [];
  const code = r.trigger?.code;
  const sections: Section[] = [{ title: '死亡记录与当时状态', rows: [
    row('记录人物', `${known(r.personName)}${id(r.personId) ? ` · 人物 #${r.personId}` : ' · 人物编号未知'}`),
    row('实际死亡记录日', day(r.day)), row('当时年龄 / 性别', `${number(r.age)} 岁 · ${known(r.sex)}`),
    row('实际触发源', triggers[code] ?? '未记录可识别的具体触发源'),
    row('触发说明', code === 'population_mixed_risk'
      ? '年龄与公共服务、饥饿、临床风险共同参与一次日常抽样；记录不提供单一疾病或饥饿的归因结论。'
      : code === 'external_age_risk' ? '外地月度年龄风险抽样；未模拟外地诊疗，不能用城内旧病史补判外地死因。'
      : code === 'suicide' ? '模拟调查记录的自杀触发；当时处境另列，不等于已证实的单一动机。'
      : ['work_accident', 'construction_collapse', 'building_collapse', 'homicide', 'road_accident'].includes(code)
        ? '来自实际事故或命案死亡入口；关联对象不直接等于责任认定，需查看对应案卷与记录。'
        : '具体原因未记录，不按年龄或并存疾病推断。'),
  ] }];
  const related: Row[] = [];
  for (const [field, kind, title] of [
    ['plotId', 'plot', '记录关联地块'], ['regionId', 'region', '记录关联地区'], ['firmId', 'firm', '记录关联企业'],
    ['projectId', 'project', '记录关联工程'], ['actorId', 'person', '记录关联人物'],
  ] as const) if (id(r[field])) related.push(row(title, ref(kind, r[field], title.replace('记录关联', ''))));
  const cases = Array.isArray(r.caseIds) ? [...new Set<number>(r.caseIds.filter(id))] : [];
  if (cases.length) related.push(row('关联案卷', ...cases.flatMap((v, i): Part[] => [...(i ? [' · '] : []), ref('case', v, '案卷')])));
  if (related.length) sections.push({ title: '实际关联对象', rows: [...related,
    row('位置与责任范围', '地块是记录关联位置，不推定精确死亡地点；关联人物、企业和工程不直接等于责任人。')] });
  if (r.risks && typeof r.risks === 'object') {
    const q = r.risks;
    sections.push({ title: '当时游戏风险输入', rows: [
      row('年龄基准（日，参考项）', risk(q.ageDaily)), row('公共服务修正后的年龄分量（日）', risk(q.ageCareDaily)),
      row('饥饿分量（日）', risk(q.starvationDaily)), row('临床分量（日）', risk(q.clinicalDaily)), row('总输入（日）', risk(q.totalDaily)),
      row('公共服务修正倍数', factor(q.careMultiplier)), row('可达医生服务效果', factor(q.accessBasedDoctorEffect)), row('环卫效果', factor(q.sanitationEffect)),
      row('风险口径', '年龄基准仅供参考；总输入使用修正后的年龄分量、饥饿和临床分量，不再次加年龄基准。这些是游戏抽样输入，不是医学死因占比；超过100%的原始输入仍按原值显示。'),
    ] });
  }
  if (r.context && typeof r.context === 'object') {
    const rows: Row[] = [];
    if (typeof r.context.monthlyRisk === 'number') rows.push(row('外地月度风险输入', risk(r.context.monthlyRisk)));
    if (Array.isArray(r.context.reasons)) {
      const reasons = r.context.reasons.filter((v: unknown): v is string => typeof v === 'string');
      if (reasons.length) rows.push(row('当时记录的调查处境', reasons.join('、')), row('处境口径', '只表示当时调查记录，不等于已证实的动机或死亡责任。'));
    }
    if (typeof r.context.scope === 'string') rows.push(row('当时观察范围', r.context.scope));
    if (rows.length) sections.push({ title: '当时处境与观察范围', rows });
  }
  const m = r.medical;
  if (!m || typeof m !== 'object' || m.enabled === false) sections.push({ title: '死亡时医疗快照', rows: [row('医疗资料', '当时未启用或未提供结构化医疗快照，既往疾病和诊疗情况未知。')] });
  else {
    const rows: Row[] = [];
    if (!Array.isArray(m.episodes)) rows.push(row('当时可见病程', '未知；未提供当时已可见病程快照。'));
    else if (!m.episodes.length) rows.push(row('当时可见病程', '没有已显现病程，不代表终身从未生病。'));
    else for (const e of m.episodes.filter((v: unknown) => v && typeof v === 'object')) {
      // 只用死亡快照内、当时已经实际确诊的诊断；不读name/cause/隐藏病种或今天的病例。
      const d = e.diagnosis;
      const confirmed = d && typeof d.name === 'string' && isDay(d.confirmed) && isDay(r.day) && d.confirmed <= r.day;
      rows.push(row(`病程 #${id(e.id) ? e.id : '未知'}`, confirmed ? `当时已确诊 ${d.name}` : '当时未确诊或确诊日期未确认，病名未知'),
        row('当时症状 / 病情指数', `${known(e.symptoms)} · ${number(e.severity, 0)}（游戏指标）`), row('病程出现日 / 当时状态', `${day(e.since)} · ${known(e.statusName)}`));
      if (confirmed) rows.push(row('当时实际检查', `${day(d.confirmed)} · `, ref('hospital', d.facility, '医院')));
      for (const s of Array.isArray(e.lastServices) ? e.lastServices : []) if (isDay(s.day) && isDay(r.day) && s.day <= r.day) rows.push(row(`最近实际完成${known(s.service)}`, day(s.day)));
    }
    if (m.admission && typeof m.admission === 'object') {
      const a = m.admission;
      rows.push(row('当时住院登记', ref('hospital', a.facility, '医院'), ` · 病程 #${id(a.episode) ? a.episode : '未知'} · 入院 ${day(a.since)}`),
        row('当时最近实际护理', isDay(a.lastCare) && isDay(r.day) && a.lastCare <= r.day ? day(a.lastCare) : '未记录当时已完成护理'),
        row('当时护理付款等待', a.waitingCoverage === true ? '等待有效付款或保障；登记床位仍保留' : a.waitingCoverage === false ? '未标记付款等待，不代表全部医嘱已经完成' : '未知'));
    } else rows.push(row('当时住院登记', Object.hasOwn(m, 'admission') ? '未登记住院' : '未知'));
    rows.push(row('当时伤后恢复期限', m.injuryUntil != null ? day(m.injuryUntil) : Object.hasOwn(m, 'injuryUntil') ? '未登记有效恢复期限' : '未知'),
      row('当时最近就诊拒绝', m.lastRefusal === null ? '当时未保留最近拒绝记录' : known(m.lastRefusal)), row('拒绝记录范围', typeof m.refusalScope === 'string' ? m.refusalScope : '患者级最近拒绝记录；不表示本次死亡由拒绝造成。'),
      row('医疗归因范围', '并存病程、护理等待或就诊拒绝不等于致死原因；不把死亡时结束跟踪写成康复。'));
    sections.push({ title: '死亡时医疗快照', rows });
  }
  sections.push({ title: '记录范围', rows: [row('说明', known(view?.scope).replace('gone另用于首次离城与月账对齐', '首次离城记录另用于月账对齐')), row('日期口径', '实际死亡记录日与首次离城、月账截止日分别保留，不用离城日期替代死亡日。')] });
  return sections;
}

/** 链接回调只接收中性种类+编号标签；其返回值是调用方提供的可信HTML。 */
export function deathHTML(view: Data | null | undefined, context: DeathDisplayContext = {}, links?: DeathLinks): string {
  return details(view, context).map(s => `<section class="sb-sec"><h4>${esc(s.title)}</h4>${s.rows.map(r => `<div class="sb-row"><span>${esc(r.label)}</span><b>${r.parts.map(p => typeof p === 'string' ? esc(p) : links?.entity(p.kind, p.id, p.label) ?? esc(p.label)).join('')}</b></div>`).join('')}</section>`).join('');
}
export function deathText(view: Data | null | undefined, context: DeathDisplayContext = {}): string[] {
  return details(view, context).flatMap(s => [s.title, ...s.rows.map(r => `${r.label}：${r.parts.map(p => typeof p === 'string' ? p : p.label).join('')}`)]);
}

/** 医院、公司等完整历史每页可有80条死亡关联；只展开所选seq的完整快照。 */
export function deathHistoryHTML(view: Data, context: { kind: string; entityId: number; seq: string; opened: ReadonlySet<string> }, links?: DeathLinks): string {
  const r = view.observed === true ? view.record : null;
  const key = `deathHistory:${context.kind}:${context.entityId}:record:${context.seq}`, open = context.opened.has(key);
  const summary = r ? `${known(r.personName)} · ${triggers[r.trigger?.code] ?? '具体触发源未知'}` : '死亡快照缺失，具体原因未知';
  return `<details data-key="${esc(key)}" data-history-detail="${esc(context.kind)}:${context.entityId}"${open ? ' open' : ''}><summary>${esc(summary)} · 展开当时记录</summary>` +
    (open ? deathHTML(view, { alive: false }, links) : '') + '</details>';
}
